import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { session } from '@/shared/session/session';

import { twoFactorApi } from '../api/auth-api';

import { authKeys } from './auth-keys';

/** Status drives the security page and `twoFactorEnabled` on /me drives the account badge. */
async function refreshTwoFactor(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: authKeys.twoFactor() }),
    session.refreshSession(queryClient),
  ]);
}

/** A fresh secret each time; the pending one is replaced until a code confirms it. */
export function useSetUpTwoFactor() {
  return useMutation({ mutationFn: twoFactorApi.setup, meta: { silentError: true } });
}

/**
 * No refresh on success: the security page swaps the setup dialog out once status says enabled,
 * which would unmount the recovery codes before they are saved. The dialog calls
 * `useRefreshTwoFactor` when the user is done.
 */
export function useEnableTwoFactor() {
  return useMutation({ mutationFn: twoFactorApi.enable, meta: { silentError: true } });
}

export function useRefreshTwoFactor() {
  const queryClient = useQueryClient();
  return useCallback(() => refreshTwoFactor(queryClient), [queryClient]);
}

export function useRegenerateRecoveryCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: twoFactorApi.regenerateRecoveryCodes,
    meta: { silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: authKeys.twoFactor() }),
  });
}

export function useDisableTwoFactor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: twoFactorApi.disable,
    meta: { successMessage: 'auth:security.disabledToast', silentError: true },
    onSuccess: () => refreshTwoFactor(queryClient),
  });
}
